import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Badge } from "@/components/Shared";

export default function Adjournments() {
  const { user } = useAuth();
  const isArb = user?.role === "arbitrator";
  const [rows, setRows] = useState([]);

  const load = () => api.get("/adjournments").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    try {
      await api.post(`/adjournments/${id}/decide`, { action, remarks: action === "allow" ? "Allowed" : "Rejected" });
      toast.success(`Request ${action === "allow" ? "allowed" : "rejected"} — parties intimated`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="adjournments-page">
      <PageHeader
        eyebrow="Requests"
        title="Adjournment Requests"
        description="Requests must be raised at least 4 days before the scheduled sitting, and only where advocates for both parties are on record."
      />
      {rows.length === 0 ? (
        <EmptyState testId="adjournments-empty" title="No adjournment requests" hint="Raise a request from the proceedings tab of a case file." />
      ) : (
        <div className="border border-border bg-card overflow-x-auto" data-testid="adjournments-table">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-border bg-muted/60">
                {["Case", "Sitting", "Sought by", "Reason", "Preferred", "Status", ""].map((h) => (
                  <th key={h} className="p-4 text-left label-caps font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-accent transition-colors duration-200" data-testid={`adjournment-list-row-${a.id}`}>
                  <td className="p-4">
                    <Link to={`/cases/${a.case_id}`} className="font-mono text-xs text-blue-900 dark:text-blue-400 hover:underline">{a.ref_id}</Link>
                    <p className="text-xs text-muted-foreground mt-1">{a.case_title}</p>
                  </td>
                  <td className="p-4 whitespace-nowrap">{fmtDateTime(a.scheduled_at)}</td>
                  <td className="p-4">{a.requested_by_name}</td>
                  <td className="p-4 text-muted-foreground max-w-[260px]">{a.reason}</td>
                  <td className="p-4 whitespace-nowrap text-muted-foreground">{fmtDate(a.preferred_date)}</td>
                  <td className="p-4">
                    <Badge className={a.status === "allowed" ? "bg-green-50 text-green-900" : a.status === "rejected" ? "bg-red-50 text-red-900" : "bg-amber-50 text-amber-900"}>
                      {a.status}
                    </Badge>
                  </td>
                  <td className="p-4">
                    {isArb && a.status === "pending" && (
                      <div className="flex gap-3">
                        <button data-testid={`adjournment-allow-${a.id}`} onClick={() => decide(a.id, "allow")}
                          className="text-xs uppercase tracking-wider text-blue-900 dark:text-blue-400">Allow</button>
                        <button data-testid={`adjournment-reject-${a.id}`} onClick={() => decide(a.id, "reject")}
                          className="text-xs uppercase tracking-wider text-red-800">Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
